/**
 * ThemeSelectionWrapper - Shows theme selection modal on first login
 * Wraps protected routes to check if user needs to select theme preference
 */
import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { ThemeSelectionModal } from './common';
import { getLandlordProfile } from '../api/landlordApi';
import { error as logError } from '../utils/logger';
import { getStorageItem } from '../utils/storage';
import type { ApiRecord } from '../types';

interface ThemeSelectionWrapperProps {
  children: ReactNode;
}

const ThemeSelectionWrapper = ({ children }: ThemeSelectionWrapperProps) => {
  const { user, updateUser } = useAuth() as { user: ApiRecord | null; updateUser: (data: ApiRecord) => void };
  const { themePreference, setTheme } = useTheme() as { themePreference: string; setTheme: (pref: string) => Promise<void> };
  const [showModal, setShowModal] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    // Only check once when user is loaded
    if (!hasChecked && user) {
      // config.ui (new) or preferences.ui / ui_preferences (legacy)
      const hasUIPreferences = (user?.config?.ui != null) || (user?.preferences?.ui != null) || (user?.ui_preferences != null);

      // Also check for legacy theme_preference (backward compatibility)
      const hasLegacyTheme = user?.theme_preference !== null && user?.theme_preference !== undefined;

      // Check localStorage as fallback (using safe utility)
      const hasLocalPreference = getStorageItem('themePreference');

      // Show modal if no preferences are set (first login)
      // Only show if user doesn't have ui_preferences AND no legacy theme AND no local preference
      const needsSelection = !hasUIPreferences && !hasLegacyTheme && !hasLocalPreference;

      if (needsSelection) {
        setShowModal(true);
      }
      setHasChecked(true);
    }
  }, [user, hasChecked]);

  const handleThemeSelect = async (selectedTheme: string) => {
    try {
      // Save theme preference (this will update backend)
      await setTheme(selectedTheme);

      // Refresh user data to get updated preferences from backend
      if (user?.landlord_id) {
        try {
          const updatedUser = await getLandlordProfile();
          // Update auth context with fresh user data (includes ui_preferences)
          updateUser(updatedUser);
        } catch (err) {
          logError('Failed to refresh user data:', err);
          // Continue anyway - preference is saved
        }
      }

      setShowModal(false);
    } catch (err) {
      logError('Failed to save theme preference:', err);
      // Still close modal - preference is saved locally
      setShowModal(false);
    }
  };

  return (
    <>
      {children}
      <ThemeSelectionModal
        isOpen={showModal}
        onSelect={handleThemeSelect}
        currentTheme={themePreference || 'dark'}
      />
    </>
  );
};

export default ThemeSelectionWrapper;
